const post = {
  slug: 'youtube-hook-writing',
  cluster: 'topics',
  eyebrow: 'Packaging',
  title: 'Writing an opening that survives the first thirty seconds',
  description:
    'Where most retention is lost, what the opening actually has to do, and four openings that work with the specific reason each one does.',
  keywords: [
    'youtube hook', 'video intro', 'first 30 seconds', 'youtube retention',
    'how to start a video', 'video opening', 'hook writing',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-hook-writing.webp',
    alt: 'A retention curve with the steepest drop highlighted in the opening seconds.',
  },
  lede:
    'More viewers leave in the first thirty seconds than in the rest of the video combined. The opening is not a warm-up — it is the part with the highest concentration of decisions being made about you.',
  sections: [
    {
      heading: 'What the opening has to do',
      body: [
        {
          type: 'para',
          text: 'Exactly one thing: **confirm the promise the thumbnail made, and raise the stakes on it.**',
        },
        {
          type: 'para',
          text: 'Someone clicked because of a specific curiosity. They are now checking whether this video is about that. Every second spent on something else is a second spent inviting them to leave.',
        },
      ],
    },
    {
      heading: 'What kills openings',
      body: [
        {
          type: 'list',
          items: [
            '**A channel introduction.** Nobody has decided they care who you are yet. Introducing yourself first is asking for a commitment before delivering anything.',
            '**Restating the title.** They read it. Repeating it as the first sentence delivers zero new information at the moment of highest attrition.',
            '**Slow scene-setting.** "For centuries, humans have wondered…" is a sentence that could open any video ever made.',
            '**Housekeeping.** Like, subscribe, notifications, sponsor. All of it is a cost you are charging before providing value.',
            '**Answering the question immediately.** The opposite failure, and more common than you would think. Resolve the hook in the first twenty seconds and there is no reason to stay for the remaining eight minutes.',
          ],
        },
      ],
    },
    {
      heading: 'Four openings that work',
      body: [
        {
          type: 'sub',
          text: 'The concrete moment',
        },
        {
          type: 'para',
          text: 'Drop straight into a scene with a time, a place and a person. It works because it is immediately specific and the viewer wants to know how it resolves.',
        },
        {
          type: 'example',
          bad: 'Bridge engineering has advanced significantly over the past century.',
          good: 'On a clear morning in November, a bridge that had passed its inspection six weeks earlier began to move in a way bridges do not move.',
        },
        {
          type: 'sub',
          text: 'The contradiction',
        },
        {
          type: 'para',
          text: 'State two things that cannot both be true. The viewer stays to find out which one gives. Works especially well when the thumbnail set up one half of the contradiction.',
        },
        {
          type: 'sub',
          text: 'The number that does not make sense',
        },
        {
          type: 'para',
          text: 'A real figure large or small enough to be surprising, stated flatly and not yet explained. The constraint is that it must be a figure you can defend — see [handling uncertain facts](/blogs/handling-uncertain-facts-in-scripts).',
        },
        {
          type: 'sub',
          text: 'The direct question, asked precisely',
        },
        {
          type: 'para',
          text: 'The most straightforward, and it works when the question is specific enough to be interesting on its own. Weak as a broad question ("why do planes fly?"), strong as a narrow one ("why is the last row of seats the most expensive on some flights?").',
        },
      ],
    },
    {
      heading: 'Then keep it open',
      body: [
        {
          type: 'para',
          text: 'The opening creates the question. The body has to visibly not answer it yet — while making progress, so the viewer can tell they are getting somewhere.',
        },
        {
          type: 'para',
          text: 'Write down the question and the point where it gets answered before drafting. The gap is the video. See [open loops](/blogs/youtube-retention-open-loops).',
        },
      ],
    },
    {
      heading: 'Where housekeeping goes',
      body: [
        {
          type: 'para',
          text: 'After the first real payoff, if at all. Once the viewer has received something, a brief ask is a reasonable exchange. Before that, it is a toll booth on an empty road.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'How long should the hook be?',
      a: 'Long enough to establish the question, short enough that the body starts before attention lapses. In practice this is often fifteen to thirty seconds, but the real constraint is that nothing in it is filler.',
    },
    {
      q: 'Should I put the best moment of the video at the start as a teaser?',
      a: 'It can work, and it carries a real risk: showing the payoff can satisfy the curiosity that would have kept someone watching. A teaser that raises a question is safer than one that delivers the answer.',
    },
  ],
  related: ['youtube-retention-open-loops', 'youtube-title-and-thumbnail', 'youtube-script-writing'],
};

export default post;
